import EpiApiClient from "@/services/serviceClients/epiApiClient";
import ApimApiClient from "@/services/serviceClients/apimApiClient";
import { withEnglishLanguage } from "@/utilities/cookies";
import { 
  EXPORT_INVOICES_API_PATH, 
  EXPORT_HISTORICAL_ORDERS_API_PATH, 
  ORDER_TEMPLATES_API_PATH,
  EXPORT_ORDERS_API_PATH,
  USER_ADD_FAVORITE_API_PATH,
  USER_REMOVE_FAVORITE_API_PATH,
  ORDERS_API_PATH,
  PRODUCT_CATALOG_API_PATH,
  ORDER_DETAILS_API_PATH,
  MEASUREMENT_CATEGORIES_API_PATH,
  SEARCH_DELIVERIES_API_PATH,
  SUBMIT_ORDER_NEW_API_PATH,
  SUBMIT_ORDER_LEGACY_API_PATH,
  ORDER_FEEDBACK_API_PATH,
  ORDER_FEEDBACK_SUBMITTED_API_PATH
} from "@/config/constants";

const LEGACY_ORDER_DTO_API_PATH = "/api/services/orders/orderdto";
const ORDER_DTO_V3_API_PATH = "/api/services/orders/v3/orderdto";
const INVOICE_DTO_V3_API_PATH = "/api/services/orders/v3/invoicedto";
const DRAFT_DTO_V3_API_PATH = "/api/services/orders/v3/draftdto";
const ESTIMATE_DTO_V3_API_PATH = "/api/services/orders/v3/estimate-to-order";
const TEMPLATE_LOAD_DTO_V3_API_PATH = "/api/services/orders/v3/templateload";
const APPLY_TEMPLATE_V3_API_PATH = "/api/services/orders/v3/applytemplate";
const SWITCH_CONTEXT_V3_API_PATH = "/api/services/orders/v3/switch-context";

class ordersService {
  static normalizeOrderDtoResponse(response) {
    if (!response) return response;

    return response?.data ?? response;
  }

  static async getTemplateLoadDto(templateId, branchCode = null) {
    const query = branchCode ? `?branchCode=${encodeURIComponent(branchCode)}` : "";
    const url = `${TEMPLATE_LOAD_DTO_V3_API_PATH}/${templateId}${query}`;

    try {
      const response = await EpiApiClient.get(url);
      return {
        success: true,
        data: ordersService.normalizeOrderDtoResponse(response.data)
      };
    } catch (error) {
      const message =
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message ||
        "request failed";

      return {
        success: false,
        error: message,
        status: error?.response?.status,
        data: error?.response?.data || {}
      };
    }
  }

  static async getEstimateDto(payload) {
    try {
      const response = await EpiApiClient.post(ESTIMATE_DTO_V3_API_PATH, payload);
      return {
        success: true,
        data: ordersService.normalizeOrderDtoResponse(response.data)
      };
    } catch (error) {
      const message =
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message ||
        "request failed";

      return {
        success: false,
        error: message,
        status: error?.response?.status,
        data: error?.response?.data || {}
      };
    }
  }

  static async applyTemplateV3(payload) {
    try {
      const response = await EpiApiClient.post(APPLY_TEMPLATE_V3_API_PATH, payload);
      return {
        success: true,
        data: ordersService.normalizeOrderDtoResponse(response.data)
      };
    } catch (error) {
      const message =
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message ||
        "request failed";

      return {
        success: false,
        error: message,
        status: error?.response?.status,
        data: error?.response?.data || {}
      };
    }
  }

  /**
   * Re-resolves the items currently in the order wizard against a different branch and/or ship-to.
   * @param {Object} payload - { branchCode, shipToSequenceNumber, deliveryType, requestDate, items }
   */
  static async switchContext(payload) {
    try {
      const response = await EpiApiClient.post(SWITCH_CONTEXT_V3_API_PATH, payload);
      return {
        success: true,
        data: ordersService.normalizeOrderDtoResponse(response.data)
      };
    } catch (error) {
      const message =
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message ||
        "request failed";

      return {
        success: false,
        error: message,
        status: error?.response?.status,
        data: error?.response?.data || {}
      };
    }
  }

   static async getDraftDto(draftId) {
     const url = `${DRAFT_DTO_V3_API_PATH}/${draftId}`;

    try {
      const response = await EpiApiClient.get(url);
      return {
        success: true,
        data: ordersService.normalizeOrderDtoResponse(response.data)
      };
    } catch (error) {
      const message =
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message ||
        "request failed";

      return {
        success: false,
        error: message,
        status: error?.response?.status,
        url,
        data: error?.response?.data || {}
      };
    }
  }

  static async getInvoicesExcelData({
    searchTerm,
    take,
    skip,
    filter,
    onlyShowFavorites,
    weatherPage
  }) {
    try {
      const response = await EpiApiClient.post(
        EXPORT_INVOICES_API_PATH,
        {
          Take: take,
          Skip: skip,
          OrderBy: ['InvoiceSort', 'InvoiceContent/InvoiceHeader/InvoiceDate desc'],
          SearchTerm: searchTerm,
          Filter: filter,
          OnlyShowFavorites: onlyShowFavorites,
          WeatherPageInvoicesToExcel: weatherPage
        },
        { responseType: 'arraybuffer' }
      );
      return {
        success: true,
        data: response.data,
        contentType: response.headers['content-type']
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error_description || "request failed",
        data: {}
      };
    }
  }

  static async getShowPostOrdersData(payload) {
    try {
      const response = await EpiApiClient.post(
        EXPORT_HISTORICAL_ORDERS_API_PATH,
        payload
      );
      return { success: true, response: response?.data?.Results };
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error_description
        : "request failed";
      return { success: false, error: errorMessage };
    }
  }

  static async fetchOrderTemplates(lang, includeItems = true) {
    const response = await EpiApiClient.get(`${ORDER_TEMPLATES_API_PATH}${lang}&includeItems=${includeItems}`);
    return response.data;
  }

  static async getOrdersExcelData({
    includeOrderStatus,
    skip,
    searchTerm,
    filter,
    onlyShowFavorites,
    take
  }) {
    try {
      const response = await withEnglishLanguage(() => 
        EpiApiClient.post(
          EXPORT_ORDERS_API_PATH,
          {
            includeOrderStatus,
            excludeOrderStatus: 'Invoiced',
            skip,
            orderBy: ['Content/OrderHeader/DeliveryDate desc'],
            searchTerm: searchTerm.length >= 3 ? searchTerm : "",
            filter,
            onlyShowFavorites,
            take,
          },
          { responseType: 'arraybuffer' }
        )
      );
      
      return {
        success: true,
        data: response.data,
        contentType: response.headers['content-type']
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error_description || "request failed",
        data: {}
      };
    }
  }

  static async setFavorite(action, orderId) {
    try {
      const favoriteApiPath = action === 'AddAsFavorite' ? USER_ADD_FAVORITE_API_PATH : USER_REMOVE_FAVORITE_API_PATH;
      console.log("favoriteApiPath:", favoriteApiPath);
      await EpiApiClient.get(`${favoriteApiPath}?number=${orderId}`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error_description || "request failed"
      };
    }
  }

  static async fetchOrdersData(payload) {
    try {
      const response = await withEnglishLanguage(() => 
        EpiApiClient.post(ORDERS_API_PATH, payload)
      );
      
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error_description || "request failed",
        data: {}
      };
    }
  }

  static async fetchProductDetailsByItemCodes(payload, lang) {
    try {
      const { data } = await EpiApiClient.post(
        `${PRODUCT_CATALOG_API_PATH}${lang}`,
        {
          branchCode: payload.branchCode,
          itemCodes: payload.itemCodes,
          lang: lang
        }
      );
      
      if (data?.ErrorCode || data?.Data === null) {
        console.warn('byItemCodes returned ErrorCode', data.ErrorCode, data.ValidationErrors);
      }
      return { success: true, data: data?.Data || [] };
    } catch (err) {
      console.error('Failed to fetch product details:', err);
      return { success: false, error: err };
    }
  }

  static async fetchOrderDetailsById(orderId, lang) {
    return EpiApiClient.get(
      `${ORDER_DETAILS_API_PATH}/${orderId}?lang=${lang}`
    );
  }

  static async fetchMeasurementCategories() {
    try {
        const response = await EpiApiClient.get(`${MEASUREMENT_CATEGORIES_API_PATH}`);
        return {
            success: true,
            data: response.data,
            contentType: response.headers['content-type']
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.error_description || "request failed",
            data: {}
        };
    }
}

  static async fetchOpenDeliveries(payload, lang) {
    try {
      const res = await EpiApiClient.post(`${SEARCH_DELIVERIES_API_PATH}${lang}`, payload);
      return { success: true, response: res.data };
    } catch (error) {
      return { success: false, error: error.message || "request failed" };
    }
  }

  static async submitOrder(dto) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-Correlation-Id': crypto.randomUUID() // Generate a unique correlation ID
      };
      
      const response = await ApimApiClient.post(SUBMIT_ORDER_NEW_API_PATH, dto, {
        headers
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      const errorMessage = error.response?.data?.ModelState 
        ? error.response.data.ModelState
        : error.response?.data?.error_description || "request failed";
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  }


  static async submitOrderLegacy(dto) {
    
    try {
      const res = await EpiApiClient.post(SUBMIT_ORDER_LEGACY_API_PATH, dto);

      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: error.message || "request failed" };
    }
  }

   static async getOrderDto(orderNumber, isReorder, invoiceNumber = null, isNewPC = false) {
    let url = !isNewPC
      ? `${LEGACY_ORDER_DTO_API_PATH}/${orderNumber}?isReorder=${isReorder}`
      : `${ORDER_DTO_V3_API_PATH}/${orderNumber}`;

    if (invoiceNumber !== null) {
      url = !isNewPC
        ? `${LEGACY_ORDER_DTO_API_PATH}/${invoiceNumber}?isReorder=${isReorder}&isInvoice=true`
        : `${INVOICE_DTO_V3_API_PATH}/${invoiceNumber}`;
    }

    try {
      const response = await EpiApiClient.get(url);
      return {
        success: true,
        data: ordersService.normalizeOrderDtoResponse(response.data)
      };
    } catch (error) {
      const message =
        error?.response?.data?.error_description ||
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message ||
        "request failed";

      return {
        success: false,
        error: message,
        status: error?.response?.status,
        url,
        data: error?.response?.data || {}
      };
    }
  }

  static async submitDeliveryFeedback(payload) {
    try {
      const response = await EpiApiClient.post(ORDER_FEEDBACK_API_PATH, payload);
      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error_description || 
        error.response?.data?.message ||
        error.message || 
        "Failed to submit delivery feedback";
      console.error('ordersService.submitDeliveryFeedback failed:', errorMessage, error);
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  }

  static async checkFeedbackSubmitted(orderNumber) {
    try {
      const response = await EpiApiClient.get(`${ORDER_FEEDBACK_SUBMITTED_API_PATH}/${orderNumber}/feedback/submitted`);
      return {
        success: true,
        data: response.data?.Data,
        error: null
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error_description || 
        error.response?.data?.message ||
        error.message || 
        "Failed to check feedback status";
      console.error('ordersService.checkFeedbackSubmitted failed:', errorMessage, error);
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  }
}
export default ordersService;
